import { useEffect, useRef, useState } from 'react';

const PULSE_ANIMATION_DURATION_MS = 500;

/**
 * ### usePulseNextButton
 *
 * @description
 * Tracks the Next button disabled state and returns whether pulse animation should be shown
 * when the button transitions from disabled to enabled.
 *
 * @example
 * const shouldPulseNextButton = usePulseNextButton({
 *   nextDisabled,
 *   nextLoading,
 * });
 *
 * @param params - Hook params
 * @param params.nextDisabled - Whether the Next button is disabled
 * @param params.nextLoading - Whether the Next button is loading
 * @returns Whether the pulse class should be applied
 */
export const usePulseNextButton = ({
  nextDisabled,
  nextLoading,
}: {
  nextDisabled?: boolean;
  nextLoading?: boolean;
}) => {
  const previousNextDisabledRef = useRef(Boolean(nextDisabled));
  const [shouldPulseNextButton, setShouldPulseNextButton] = useState(false);

  useEffect(() => {
    const wasNextDisabled = previousNextDisabledRef.current;
    const isNextDisabled = Boolean(nextDisabled);
    previousNextDisabledRef.current = isNextDisabled;

    if (!wasNextDisabled || isNextDisabled || nextLoading) {
      return;
    }

    setShouldPulseNextButton(false);

    const animationFrameId = window.requestAnimationFrame(() => {
      setShouldPulseNextButton(true);
    });

    const timeoutId = window.setTimeout(() => {
      setShouldPulseNextButton(false);
    }, PULSE_ANIMATION_DURATION_MS);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [nextDisabled, nextLoading]);

  return shouldPulseNextButton;
};
